// One invoice in the drawer beside the list. A stopped one leads with the problem as a headline, one Invoice – PO
// comparison and the ways out (shared/problem.tsx), the same block the full review shows. A check stays on the invoice
// until its result is in; a reject moves on to the next. The checks and the technical details are on the full review.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api, apiBlob, apiUpload } from "../api/client";
import type { Invoice, InvoiceDetail, UploadResult } from "../api/types";
import { useApiMutation, useBatchSchedule, useInvoice, useSettings } from "../api/hooks";
import { useSession } from "../auth/SessionProvider";
import { checksOf } from "../shared/checks";
import { CheckIcon, ErrorCard, LoadingRows, OutcomePill, ProgressCompact, muted, overline } from "../shared/components";
import { fmtWhen, invoiceRef, money, outcomeOf, stepIndex, poRef } from "../shared/format";
import { isWorking, setupGapOf } from "../shared/ladder";
import { ChecksCompact, resolvedBy } from "../shared/invoiceParts";
import { Compare, PlanView, ProblemHead, planOf, problemOf } from "../shared/problem";
import { Button, Dialog, Icon, IconButton, useToast } from "../ui";
import { approveLine } from "./approval";
import { AsInvoicedDialog, RejectDialog, ReplaceDialog, SetPoDialog } from "./Dialogs";

export interface DetailContext {
  label: string;
  index: number;
  total: number;
  prev: () => void;
  next: () => void;
}

const PENDING_MS = 8000;

/** The actions every invoice screen shares, with their dialogs. Buttons go away while work runs. `onActed` hears
 *  which invoices were rejected or replaced, so the slide-out can move on to the next one; an action that checks
 *  again leaves the invoice open, so its progress and result show where it was asked for. */
export function useInvoiceActions(opts: { onActed?: (ids: string[]) => void } = {}) {
  const acted = (ids: string[]) => opts.onActed?.(ids);
  const toast = useToast();
  const session = useSession();
  const schedule = useBatchSchedule();
  const settings = useSettings();
  const currency = session.tenant?.currency ?? "USD";
  const [dialog, setDialog] = useState<{ kind: "resolve" | "setpo" | "asinvoiced" | "limit" | "replace"; invoice: Invoice; file?: File } | null>(null);
  // The corrected-invoice picker lives in `dialogs`, so it is on the page wherever the actions are.
  const fileRef = useRef<HTMLInputElement>(null);
  // Ids whose work was just started: treated as working until the API shows them queued or finished.
  const [pending, setPending] = useState<Record<string, number>>({});
  const mark = (ids: string[]) => {
    const until = Date.now() + PENDING_MS;
    setPending((p) => ({ ...p, ...Object.fromEntries(ids.map((id) => [id, until])) }));
    window.setTimeout(() => setPending((p) => { const n = { ...p }; for (const id of ids) if (n[id] && n[id] <= Date.now()) delete n[id]; return n; }), PENDING_MS + 50);
  };
  // A rejected or replaced invoice leaves the Needs you list the moment it is confirmed, with a note for its leaving
  // row ("Rejecting…"), rather than when the API answers; a failure puts it back.
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState<Record<string, string>>({});
  const leaveNow = (id: string, note: string) => {
    setNotes((n) => ({ ...n, [id]: note }));
    queryClient.setQueriesData<{ items: Invoice[] }>({ predicate: (q) => q.queryKey[0] === "needs" && q.queryKey.length === 2 }, (old) => (old?.items ? { ...old, items: old.items.filter((i) => i.id !== id) } : old));
  };
  const undoLeave = (id: string) => {
    setNotes((n) => { const c = { ...n }; delete c[id]; return c; });
    void queryClient.invalidateQueries({ queryKey: ["needs"] });
  };
  const reprocess = useApiMutation((ids: string[]) => api("/invoices/reprocess", { method: "POST", body: { invoiceIds: ids } }));
  const dismiss = useApiMutation(({ id, comment }: { id: string; comment: string }) => api(`/invoices/${id}/dismiss`, { method: "POST", body: { comment } }));
  const setPo = useApiMutation(({ id, po }: { id: string; po: string }) => api(`/invoices/${id}/set-po`, { method: "POST", body: { po } }));
  const acceptSupplier = useApiMutation((id: string) => api(`/invoices/${id}/accept-supplier`, { method: "POST", body: {} }), [["invoices"], ["invoice"], ["dashboard"], ["needs"], ["settings"], ["aliases"]]);
  const fix = useApiMutation(({ ids, kind }: { ids: string[]; kind: "AsInvoiced" | "Limit" }) => api("/invoices/fix", { method: "POST", body: { invoiceIds: ids, fix: kind } }));
  // Replace: the corrected PDF goes through intake like any upload; once accepted, this invoice is rejected as replaced.
  const replaceWith = useApiMutation(async ({ invoice, file }: { invoice: Invoice; file: File }) => {
    const form = new FormData();
    form.append("files", file, file.name);
    const r = await apiUpload<UploadResult>("/intake/upload", form, () => {});
    if (!r.accepted.length) throw new Error(r.rejected[0]?.reason ?? (r.duplicates.length ? "That PDF was already received" : "The file was not accepted"));
    await api(`/invoices/${invoice.id}/dismiss`, { method: "POST", body: { comment: "Replaced by " + file.name } });
    return r;
  });
  const nextRun = schedule.data?.next?.[0] ?? null;
  const policy = settings.data?.release.policy ?? "Immediate";
  const readyLine = approveLine(policy, nextRun);

  const checkAgain = (ids: string[]) => { mark(ids); reprocess.mutate(ids, {
    onSuccess: () => { toast({ tone: "info", title: "Checking now", message: "Usually under a minute" }); },
    onError: (e) => toast({ tone: "error", title: "Couldn’t check again", message: e.message }),
  }); };
  const supplierOk = (id: string) => { mark([id]); acceptSupplier.mutate(id, {
    onSuccess: () => { toast({ tone: "success", title: "Supplier remembered", message: "Checking again with the supplier accepted" }); },
    onError: (e) => toast({ tone: "error", title: "Couldn’t accept the supplier", message: e.message }),
  }); };

  // Reject with a reason: the row leaves at once, and comes back if the API refuses.
  const rejectWith = (invoice: Invoice, comment: string) => {
    leaveNow(invoice.id, "Rejecting");
    dismiss.mutate({ id: invoice.id, comment }, {
      onSuccess: () => { acted([invoice.id]); toast({ tone: "success", title: "Rejected", message: [invoice.supplier, invoice.invoiceNumber].filter(Boolean).join(" – ") }); },
      onError: (e) => { undoLeave(invoice.id); toast({ tone: "error", title: "Couldn’t reject it", message: e.message }); },
    });
  };

  const dialogs: ReactNode = (
    <>
      <input ref={fileRef} type="file" accept=".pdf" style={{ display: "none" }} onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; setDialog((d) => (d && d.kind === "replace" && file ? { ...d, file } : d && d.kind === "replace" ? null : d)); }} />
      {dialog && (dialog.kind === "resolve" ? (
      <RejectDialog invoice={dialog.invoice} currency={currency} busy={dismiss.isPending} onCancel={() => setDialog(null)} onConfirm={(comment) => { setDialog(null); rejectWith(dialog.invoice, comment); }} />
    ) : dialog.kind === "replace" ? (
      dialog.file ? <ReplaceDialog invoice={dialog.invoice} file={dialog.file} currency={currency} busy={replaceWith.isPending} onCancel={() => setDialog(null)} onConfirm={() => { leaveNow(dialog.invoice.id, "Replacing"); setDialog(null); replaceWith.mutate({ invoice: dialog.invoice, file: dialog.file! }, { onSuccess: () => { acted([dialog.invoice.id]); toast({ tone: "success", title: "Replacing", message: `Checking ${dialog.file!.name} now; ${dialog.invoice.invoiceNumber ?? "this one"} is marked Not approved` }); }, onError: (e) => { undoLeave(dialog.invoice.id); toast({ tone: "error", title: "Couldn’t replace it", message: e.message }); } }); }} /> : null
    ) : dialog.kind === "setpo" ? (
      <SetPoDialog invoice={dialog.invoice} currency={currency} busy={setPo.isPending} onCancel={() => setDialog(null)} onConfirm={(po) => { mark([dialog.invoice.id]); setPo.mutate({ id: dialog.invoice.id, po }, { onSuccess: () => { setDialog(null); toast({ tone: "success", title: "PO " + po + " set", message: "Checking against it now" }); }, onError: (e) => toast({ tone: "error", title: "Couldn’t set the PO", message: e.message }) }); }} />
    ) : dialog.kind === "limit" ? (
      <Dialog open onClose={() => setDialog(null)} title={`Approve PO ${poRef(dialog.invoice) ?? ""} over your limit?`} description={`Every check passed. ${dialog.invoice.summary ?? "The total is over your limit."} The PO is approved in Syncore now, and the limit isn’t applied to this invoice again.`} footer={<><Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button><Button loading={fix.isPending} leadingIcon={<Icon name="check" size={16} />} onClick={() => { mark([dialog.invoice.id]); fix.mutate({ ids: [dialog.invoice.id], kind: "Limit" }, { onSuccess: () => { setDialog(null); toast({ tone: "success", title: "Approving now", message: "Usually under a minute" }); }, onError: (e) => toast({ tone: "error", title: "Couldn’t approve", message: e.message }) }); }}>Approve now</Button></>} />
    ) : (
      <AsInvoicedDialog invoice={dialog.invoice} currency={currency} busy={fix.isPending} onCancel={() => setDialog(null)} onConfirm={() => { mark([dialog.invoice.id]); fix.mutate({ ids: [dialog.invoice.id], kind: "AsInvoiced" }, { onSuccess: () => { setDialog(null); toast({ tone: "success", title: "Approving as invoiced", message: "Updating the PO in Syncore, then checking again – usually under a minute" }); }, onError: (e) => toast({ tone: "error", title: "Couldn’t approve as invoiced", message: e.message }) }); }} />
    ))}
    </>
  );

  return {
    currency, readyLine, nextRun, policy, dialogs,
    checkAgain, supplierOk, mark,
    resolve: (invoice: Invoice) => setDialog({ kind: "resolve", invoice }),
    /** Reject straight away with a given reason, no dialog: a duplicate's reason is "Duplicate". */
    rejectNow: (invoice: Invoice, reason: string) => rejectWith(invoice, reason),
    /** Opens the picker; the dialog follows once a PDF is chosen. */
    replace: (invoice: Invoice) => { setDialog({ kind: "replace", invoice }); fileRef.current?.click(); },
    setPo: (invoice: Invoice) => setDialog({ kind: "setpo", invoice }),
    /** "Approve as invoiced" on a freight or lines difference: the PO is made to match the invoice in Syncore, checked again and approved. */
    approveAsInvoiced: (invoice: Invoice) => setDialog({ kind: "asinvoiced", invoice }),
    /** "Approve now" on an Over your limit invoice: the limit is waived for it and the PO approved. */
    approveOverLimit: (invoice: Invoice) => setDialog({ kind: "limit", invoice }),
    /** Working: the API says so, or we just asked it to. */
    working: (invoice: Pick<Invoice, "id" | "status">) => isWorking(invoice) || (pending[invoice.id] ?? 0) > Date.now(),
    /** "Rejecting" or "Replacing" for a row that left Needs you before the API answered. */
    notes,
    busy: reprocess.isPending || dismiss.isPending || setPo.isPending || acceptSupplier.isPending || fix.isPending || replaceWith.isPending,
  };
}

export type InvoiceActions = ReturnType<typeof useInvoiceActions>;

/** The checking strip while a check runs: green once a missing setting is saved, blue otherwise. */
function CheckingNow({ invoice }: { invoice: Invoice }) {
  return setupGapOf(invoice) ? (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "12px 16px", borderRadius: "var(--radius-lg)", background: "var(--status-matched-bg)", boxShadow: "inset 0 0 0 1px rgba(20,184,138,.35)", font: "700 15px var(--font-body)", color: "var(--status-matched-fg)" }}><CheckIcon r="ok" size={18} />Syncore connected – checking again now<span style={{ flex: 1 }} /><ProgressCompact step={stepIndex(invoice.step)} fit /></div>
  ) : (
    <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "12px 16px", borderRadius: "var(--radius-lg)", background: "var(--av-blue-50)", boxShadow: "inset 0 0 0 1px var(--av-blue-200)" }}><span style={{ ...overline, color: "var(--av-blue-700)" }}>Checking now</span><ProgressCompact step={stepIndex(invoice.step)} label={invoice.step} big /></div>
  );
}

/** A stopped invoice: the problem, the comparison, then the ways out. The slide-out and the full review both show it. */
export function NeedsYou({ detail, actions }: { detail: InvoiceDetail; actions: InvoiceActions }) {
  const session = useSession();
  const navigate = useNavigate();
  const settings = useSettings();
  const inv = detail.invoice;
  const problem = problemOf(detail, actions.currency, settings.data?.freight ?? null);
  const plan = planOf(detail, actions, { navigate });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <ProblemHead problem={problem}>
        {detail.attachment && <span style={{ display: "inline-flex", alignItems: "center", gap: 6, font: "600 13px var(--font-body)", color: "var(--text-body)" }}><Icon name="paperclip" size={14} />{detail.attachment.reused ? "PDF already on the job in Syncore" : "PDF attached to the job in Syncore with a note"}</span>}
      </ProblemHead>
      <Compare rows={problem.rows} items={problem.items} />
      {actions.working(inv) ? <CheckingNow invoice={inv} /> : <PlanView plan={plan} viewer={!session.canAct} />}
    </div>
  );
}

export function ProblemDetail({ id, ctx, mode, onClose, actions }: { id: string; ctx: DetailContext | null; mode: "drawer" | "page"; onClose?: () => void; actions: InvoiceActions }) {
  const navigate = useNavigate();
  const toast = useToast();
  const detail = useInvoice(id);
  const d = detail.data;
  const checks = useMemo(() => (d ? checksOf(d) : []), [d]);
  // Moving to another invoice puts focus on its name when the button that moved it has gone (a dialog closed, the
  // panel reloaded), so a keyboard user carries on from the top of the next invoice.
  const headRef = useRef<HTMLHeadingElement>(null);
  const shown = useRef<string | null>(null);
  useEffect(() => {
    if (!d) return;
    const was = shown.current;
    shown.current = d.invoice.id;
    if (was && was !== d.invoice.id && (!document.activeElement || document.activeElement === document.body)) headRef.current?.focus({ preventScroll: true });
  }, [d]);
  // J and K move through the list, as in a mail client; not while typing or while a dialog is open.
  useEffect(() => {
    if (!ctx) return;
    const k = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || document.querySelector('[aria-modal="true"]:not([data-drawer])')) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (e.key === "j" && ctx.index < ctx.total - 1) ctx.next();
      else if (e.key === "k" && ctx.index > 0) ctx.prev();
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [ctx]);
  if (detail.isPending) return <LoadingRows rows={5} />;
  if (detail.error || !d) return <ErrorCard error={detail.error ?? new Error("Not found")} retry={() => void detail.refetch()} />;
  const inv = d.invoice;
  const o = outcomeOf(inv);
  const active = o === "rev" || o === "stop" || o === "set" || actions.working(inv);
  const po = poRef(inv);
  const poUrl = d.po?.syncoreUrl;
  const link = { border: 0, padding: 0, background: "none", font: "inherit", fontWeight: 700, color: "var(--text-link)", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 } as const;
  // The tab opens on the click, so no popup blocker stops it; the PDF arrives in it once fetched with the token.
  const viewPdf = () => {
    const tab = window.open("", "_blank");
    apiBlob(`/invoices/${inv.id}/pdf`).then(({ blob }) => {
      const url = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      if (tab) tab.location.href = url; else window.open(url, "_blank");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }).catch((e: unknown) => { tab?.close(); toast({ tone: "error", title: "Couldn’t open the PDF", message: e instanceof Error ? e.message : String(e) }); });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, minHeight: 32 }}>
        {ctx && <><span style={{ ...overline, marginRight: 4 }}>{ctx.label} – {ctx.index + 1} of {ctx.total}</span><IconButton size="sm" variant="outline" icon={<Icon name="chevron-left" size={16} />} label="Previous (K)" disabled={ctx.index === 0} onClick={ctx.prev} /><IconButton size="sm" variant="outline" icon={<Icon name="chevron-right" size={16} />} label="Next (J)" disabled={ctx.index >= ctx.total - 1} onClick={ctx.next} /></>}
        <span style={{ flex: 1 }} />
        {inv.pdfAvailable && <Button size="sm" variant="outline" leadingIcon={<Icon name="file-text" size={14} />} onClick={viewPdf}>View PDF</Button>}
        {mode === "drawer" && onClose && <IconButton size="sm" icon={<Icon name="x" size={18} />} label="Close" onClick={onClose} />}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <h2 ref={headRef} tabIndex={-1} style={{ font: "800 24px/1.2 var(--font-display)", letterSpacing: "-0.01em", margin: 0, flex: 1, minWidth: 0, outline: "none" }}>{inv.supplier ?? invoiceRef(inv)}</h2>
          {!active && <OutcomePill o={o} below readyLine={actions.readyLine} />}
          <span style={{ font: "800 20px/1.2 var(--font-display)", fontVariantNumeric: "tabular-nums", color: "var(--text-strong)" }}>{money(inv.total, actions.currency)}</span>
        </div>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "baseline", ...muted }}>
          {inv.invoiceNumber && <span>Invoice {inv.invoiceNumber}</span>}
          {po && (poUrl ? <a href={poUrl} target="_blank" rel="noopener" style={{ ...link, textDecoration: "none" }}>PO {po}<Icon name="external-link" size={13} /></a> : <span>PO {po}</span>)}
          {po && <a href={`/po/${inv.jobNumber}/${inv.poNumber}`} onClick={(e) => { e.preventDefault(); navigate(`/po/${inv.jobNumber}/${inv.poNumber}`); }} style={{ ...link, textDecoration: "none" }}>PO history</a>}
          <span>Received {fmtWhen(inv.receivedAt)}</span>
          {inv.dryRun && <span style={{ color: "var(--av-blue-700)", fontWeight: 600 }}>Preview mode – nothing changed in Syncore</span>}
        </div>
      </div>

      {active ? (
        <NeedsYou detail={d} actions={actions} />
      ) : o === "wait" || o === "prog" ? (
        <div style={{ padding: "18px 20px", borderRadius: "var(--radius-lg)", background: "var(--av-blue-50)", display: "flex", flexDirection: "column", gap: 8 }}>
          <span style={overline}>{o === "wait" ? "Queued" : "Checking"}</span>
          <ProgressCompact step={stepIndex(inv.step)} label={inv.step} big />
          {inv.nextAttemptAt && <span style={muted}>Next try {fmtWhen(inv.nextAttemptAt)}</span>}
        </div>
      ) : (
        <div style={{ padding: "16px 18px", borderRadius: "var(--radius-lg)", background: o === "res" ? "var(--surface-sunken)" : "var(--status-matched-bg)", display: "flex", flexDirection: "column", gap: 8 }}>
          <ChecksCompact checks={checks} />
          <span style={{ font: "600 13px var(--font-body)", color: "var(--text-body)" }}>
            {o === "ready" ? actions.readyLine : o === "ok" ? (inv.dryRun ? "Preview: would have been approved in Syncore" : "PO approved in Syncore") : inv.resolved ? `Not approved – ${resolvedBy(d)}: “${inv.resolved.comment}”` : ""}
          </span>
        </div>
      )}

      {mode === "drawer" && <a onClick={() => navigate(`/invoices/${inv.id}`)} style={{ cursor: "pointer", font: "700 14px var(--font-body)", display: "inline-flex", alignItems: "center", gap: 6, paddingTop: 16, borderTop: "1px solid var(--border-subtle)" }}>Open full review <Icon name="arrow-right" size={14} /></a>}
    </div>
  );
}

export function ProblemDrawer({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      // Tab goes round the slide-out, not the page behind it; a dialog open over it keeps its own Tab.
      const panel = ref.current;
      if (e.key !== "Tab" || !panel || document.querySelector('[aria-modal="true"]:not([data-drawer])')) return;
      const stops = [...panel.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
      if (!stops.length) return;
      const inside = panel.contains(document.activeElement);
      if (e.shiftKey && (!inside || document.activeElement === stops[0] || document.activeElement === panel)) { e.preventDefault(); stops[stops.length - 1].focus(); }
      else if (!e.shiftKey && (!inside || document.activeElement === stops[stops.length - 1])) { e.preventDefault(); stops[0].focus(); }
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  // Focus moves into the slide-out when it opens, and back to where it was when it closes.
  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    ref.current?.focus({ preventScroll: true });
    return () => before?.focus({ preventScroll: true });
  }, []);
  return (
    <div className="avp-drawer">
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(14,32,55,.22)", backdropFilter: "blur(2px)", animation: "avp-fade var(--dur-base) var(--ease-out)" }} />
      <aside ref={ref} role="dialog" aria-modal="true" aria-label="Invoice" data-drawer tabIndex={-1} style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: "min(600px,100%)", background: "var(--surface-page)", boxShadow: "var(--shadow-lg)", overflow: "auto", padding: "18px 28px 36px", outline: "none", animation: "avp-slide var(--dur-slow) var(--ease-out)" }}>{children}</aside>
    </div>
  );
}
